import { useState, useEffect, useRef } from "react";
import { requestGoogleCalendarSync } from '@/services/googleCalendarSync';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, Users, TrendingUp, Calendar, Phone, MessageSquare, UserCheck, UserX, AlertTriangle, Eye, Edit, Trash2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { NotificationCenter } from "@/components/appointments/NotificationCenter";
import { AppointmentDashboard } from "@/components/appointments/AppointmentDashboard";
import CalendarNotificationDemo from "@/components/CalendarNotificationDemo";

interface Appointment {
  id: string;
  title: string;
  description?: string;
  appointment_date: string;
  duration: number;
  location?: string;
  status: string;
  created_at: string;
  client_id?: string;
  case_id?: string;
  appointment_type: string;
  priority: string;
  outcome_notes?: string;
  clients?: { name: string; email: string };
  // Campos específicos para controle de prospecção
  prospect_name?: string;
  prospect_phone?: string;
  prospect_email?: string;
  meeting_subject?: string;
  conversion_status?: string; // "converted" | "not_converted" | "pending"
  will_register_as_client?: boolean;
  follow_up_required?: boolean;
  follow_up_date?: string;
  lead_source?: string;
  urgency_level?: string;
  // Novos campos
  is_billable?: boolean;
  outcome_status?: string;
  notes?: string;
}

interface Client {
  id: string;
  name: string;
  email: string;
  phone?: string;
}

interface AppointmentFormData {
  prospect_name: string;
  prospect_phone: string;
  prospect_email: string;
  meeting_subject: string;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  location: string;
  priority: string;
  description: string;
  lead_source: string;
  urgency_level: string;
  is_billable: boolean;
  outcome_status: string;
  rescheduled_date: string;
  rescheduled_time: string;
  notes: string;
}

const Appointments = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<Appointment[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOutcomeDialogOpen, setIsOutcomeDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [conversionFilter, setConversionFilter] = useState("all");
  
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>("walk_in");
  const [timers, setTimers] = useState<Record<string, { isRunning: boolean; elapsed: number }>>({});
  
  const [formData, setFormData] = useState<AppointmentFormData>({
    prospect_name: "",
    prospect_phone: "",
    prospect_email: "",
    meeting_subject: "",
    appointment_date: "",
    appointment_time: "",
    duration: 60,
    location: "",
    priority: "normal",
    description: "",
    lead_source: "",
    urgency_level: "normal",
    is_billable: false,
    outcome_status: "pending",
    rescheduled_date: "",
    rescheduled_time: "",
    notes: ""
  });

  const [outcomeData, setOutcomeData] = useState({
    conversion_status: "",
    outcome_notes: "",
    will_register_as_client: false,
    follow_up_required: false,
    follow_up_date: ""
  });

  const [activeTimer, setActiveTimer] = useState<string | null>(null);
  const timerRefs = useRef<Record<string, NodeJS.Timeout>>({});
  
  const { toast } = useToast();

  // Funções para cronômetro
  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startTimer = (appointmentId: string) => {
    if (timerRefs.current[appointmentId]) {
      clearInterval(timerRefs.current[appointmentId]);
    }
    
    setActiveTimer(appointmentId);
    setTimers(prev => ({
      ...prev,
      [appointmentId]: { 
        isRunning: true, 
        elapsed: prev[appointmentId]?.elapsed || 0 
      }
    }));
    
    timerRefs.current[appointmentId] = setInterval(() => {
      setTimers(prev => ({
        ...prev,
        [appointmentId]: { 
          isRunning: true, 
          elapsed: (prev[appointmentId]?.elapsed || 0) + 1 
        }
      }));
    }, 1000);
  };

  const stopTimer = (appointmentId: string) => {
    if (timerRefs.current[appointmentId]) {
      clearInterval(timerRefs.current[appointmentId]);
      delete timerRefs.current[appointmentId];
    }
    
    setTimers(prev => ({
      ...prev,
      [appointmentId]: { 
        ...prev[appointmentId], 
        isRunning: false 
      }
    }));
    
    if (activeTimer === appointmentId) {
      setActiveTimer(null);
    }
  };

  const resetTimer = (appointmentId: string) => {
    if (timerRefs.current[appointmentId]) {
      clearInterval(timerRefs.current[appointmentId]);
      delete timerRefs.current[appointmentId];
    }
    
    setTimers(prev => ({
      ...prev,
      [appointmentId]: { 
        isRunning: false, 
        elapsed: 0 
      }
    }));
    
    if (activeTimer === appointmentId) {
      setActiveTimer(null);
    }
  };

  // Limpar timers quando o componente for desmontado
  useEffect(() => {
    return () => {
      Object.values(timerRefs.current).forEach(clearInterval);
    };
  }, []);

  const fetchClients = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('clients')
        .select('id, name, email, phone')
        .eq('user_id', user.id)
        .order('name');

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  useEffect(() => {
    // Verificar autenticação ao carregar a página
    const checkAuth = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      console.log('=== VERIFICAÇÃO DE AUTENTICAÇÃO ===');
      console.log('User:', user);
      console.log('Error:', error);
      if (user) {
        console.log('User ID:', user.id);
        console.log('User email:', user.email);
      }
    };
    
    checkAuth();
    fetchAppointments();
    fetchClients();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [appointments, searchTerm, statusFilter, conversionFilter]);

  const fetchAppointments = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('Usuário não autenticado');
        return;
      }

      const { data, error } = await supabase
        .from('appointments')
        .select(`
          *,
          clients:client_id (name, email)
        `)
        .eq('user_id', user.id)
        .order('appointment_date', { ascending: true });

      if (error) throw error;
      console.log('Appointments fetched:', data);
      console.log('User ID used for fetch:', user.id);
      console.log('Number of appointments found:', data?.length || 0);
      setAppointments(data || []);
    } catch (error) {
      console.error('Erro ao buscar atendimentos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os atendimentos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...appointments];

    if (searchTerm) {
      filtered = filtered.filter(apt => 
        apt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        apt.prospect_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        apt.prospect_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        apt.meeting_subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        apt.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        apt.clients?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      const normStatus = (v: string) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      filtered = filtered.filter(apt => normStatus(apt.status) === normStatus(statusFilter));
    }

    if (conversionFilter !== "all") {
      filtered = filtered.filter(apt => {
        if (conversionFilter === "converted") return apt.conversion_status === "converted";
        if (conversionFilter === "not_converted") return apt.conversion_status === "not_converted";
        if (conversionFilter === "pending") return apt.conversion_status === "pending" || !apt.conversion_status;
        return true;
      });
    }

    setFilteredAppointments(filtered);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      console.log('=== INICIANDO SUBMISSÃO ===');
      
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      console.log('Auth result:', { user, authError });
      
      if (!user) {
        console.error('Usuário não autenticado');
        throw new Error('Usuário não autenticado');
      }

      console.log('FormData:', formData);
      console.log('User ID:', user.id);
      console.log('User email:', user.email);

      const appointmentDateTime = new Date(`${formData.appointment_date}T${formData.appointment_time}`);
      console.log('Appointment DateTime:', appointmentDateTime);
      console.log('Appointment DateTime ISO:', appointmentDateTime.toISOString());
      
      let dataToSave = {
        title: `Atendimento com ${formData.prospect_name}`,
        description: `${formData.meeting_subject}\n\nObservações: ${formData.description}${formData.notes ? `\n\nDescrição do Atendimento: ${formData.notes}` : ''}`,
        appointment_date: appointmentDateTime.toISOString(),
        duration: formData.duration,
        location: formData.location,
        priority: formData.priority === "média" ? "normal" : formData.priority,
        appointment_type: "atendimento",
        status: "agendado",
        // Novos campos específicos de prospecção
        prospect_name: formData.prospect_name,
        prospect_phone: formData.prospect_phone,
        prospect_email: formData.prospect_email,
        meeting_subject: formData.meeting_subject,
        lead_source: formData.lead_source,
        urgency_level: formData.urgency_level,
        conversion_status: "pending",
        is_billable: formData.is_billable,
        user_id: user.id
      };

      // Se selecionou um cliente existente, adicione o client_id
      if (selectedClient && selectedClient !== "new_client" && selectedClient !== "walk_in") {
        dataToSave = {
          ...dataToSave,
          client_id: selectedClient
        } as any;
      }

      console.log('Data to save:', dataToSave);

      let savedAppointmentId: string | null = null;

      if (editingAppointment) {
        console.log('Updating appointment:', editingAppointment.id);
        const { error } = await supabase
          .from('appointments')
          .update(dataToSave)
          .eq('id', editingAppointment.id);
        
        if (error) {
          console.error('Update error:', error);
          throw error;
        }
        savedAppointmentId = editingAppointment.id;
        requestGoogleCalendarSync(); toast({ title: "Sucesso", description: "Atendimento atualizado com sucesso" });
      } else {
        console.log('Inserting new appointment');
        const { data, error } = await supabase
          .from('appointments')
          .insert([dataToSave])
          .select()
          .single();
        
        if (error) {
          console.error('Insert error:', error);
          throw error;
        }
        console.log('Insert success:', data);
        savedAppointmentId = data.id;
        requestGoogleCalendarSync(); toast({ title: "Sucesso", description: "Atendimento agendado com sucesso" });
      }

      // Se o status for "rescheduled", criar um novo compromisso com a nova data
      if (formData.outcome_status === "rescheduled" && formData.rescheduled_date && formData.rescheduled_time) {
        const rescheduledDateTime = new Date(`${formData.rescheduled_date}T${formData.rescheduled_time}`);
        const rescheduledData = {
          ...dataToSave,
          id: undefined, // Remover o ID para criar um novo registro
          title: `Remarcação - ${dataToSave.title}`,
          appointment_date: rescheduledDateTime.toISOString(),
          status: "agendado",
          outcome_status: "pending", // Zerar status para o novo compromisso
          notes: `Remarcado do atendimento original de ${format(appointmentDateTime, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}. Motivo: ${formData.notes || 'Remarcação solicitada'}`,
          created_at: undefined, // Permitir que o banco de dados defina automaticamente
          updated_at: undefined  // Permitir que o banco de dados defina automaticamente
        } as any;

        const { error: rescheduleError } = await supabase
          .from('appointments')
          .insert([rescheduledData]);
        
        if (rescheduleError) {
          console.error('Reschedule error:', rescheduleError);
          throw rescheduleError;
        }

        // Atualizar o status do compromisso original para "remarcado"
        await supabase
          .from('appointments')
          .update({ status: "remarcado", description: `${dataToSave.description || ''}\nRemarcado para: ${format(rescheduledDateTime, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}` } as any)
          .eq('id', savedAppointmentId);

        requestGoogleCalendarSync(); toast({ title: "Sucesso", description: "Remarcação agendada com sucesso!" });
      }

      setIsDialogOpen(false);
      setEditingAppointment(null);
      resetForm();
      fetchAppointments();
    } catch (error) {
      console.error('Erro ao salvar atendimento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o atendimento",
        variant: "destructive"
      });
    }
  };

  const handleOutcomeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedAppointment) return;

    try {
      const updateData = {
        outcome_notes: `Status: ${outcomeData.conversion_status}\nResultado: ${outcomeData.outcome_notes}\nCadastrar como cliente: ${outcomeData.will_register_as_client ? 'Sim' : 'Não'}\nFollow-up necessário: ${outcomeData.follow_up_required ? 'Sim' : 'Não'}${outcomeData.follow_up_date ? `\nData follow-up: ${outcomeData.follow_up_date}` : ''}`,
        status: "concluído",
        conversion_status: outcomeData.conversion_status === "convertido" ? "converted" : 
                          outcomeData.conversion_status === "não convertido" ? "not_converted" : "em_analise",
        will_register_as_client: outcomeData.will_register_as_client,
        follow_up_required: outcomeData.follow_up_required,
        follow_up_date: outcomeData.follow_up_date || null
      };

      const { error } = await supabase
        .from('appointments')
        .update(updateData)
        .eq('id', selectedAppointment.id);
      
      if (error) throw error;

      // Se escolheu cadastrar como cliente após conversão
      if (outcomeData.will_register_as_client && outcomeData.conversion_status === "convertido") {
        // Perguntar se quer realizar o cadastro completo
        const shouldRegister = confirm(
          `O atendimento foi convertido com sucesso!\n\nDeseja realizar o cadastro completo do cliente "${selectedAppointment.prospect_name || selectedAppointment.title.replace("Atendimento com ", "")}" agora?\n\nClique em "OK" para abrir o formulário de cadastro ou "Cancelar" para fazer isso depois.`
        );

        if (shouldRegister) {
          // Usar dados específicos do prospect dos novos campos
          const clientData = {
            name: selectedAppointment.prospect_name || selectedAppointment.title.replace("Atendimento com ", ""),
            email: selectedAppointment.prospect_email || "",
            phone: selectedAppointment.prospect_phone || "",
            user_id: (await supabase.auth.getUser()).data.user?.id,
            lgpd_consent: true,
            notes: `Cliente convertido do atendimento realizado em ${format(new Date(selectedAppointment.appointment_date), "dd/MM/yyyy", { locale: ptBR })}. Fonte: ${selectedAppointment.lead_source || "Não informado"}`
          };

          const { error: clientError } = await supabase
            .from('clients')
            .insert([clientData]);

          if (clientError) {
            console.error('Erro ao criar cliente:', clientError);
            toast({ 
              title: "Erro", 
              description: "Não foi possível cadastrar o cliente automaticamente. Você pode fazer isso manualmente na página de Clientes.",
              variant: "destructive"
            });
          } else {
            toast({ 
              title: "Sucesso", 
              description: "Cliente cadastrado automaticamente! Você pode editar os dados completos na página de Clientes." 
            });
          }
        } else {
          toast({ 
            title: "Informação", 
            description: "Cliente marcado para conversão. Você pode cadastrá-lo posteriormente na página de Clientes." 
          });
        }
      }

      requestGoogleCalendarSync(); toast({ title: "Sucesso", description: "Resultado do atendimento salvo com sucesso" });
      setIsOutcomeDialogOpen(false);
      setSelectedAppointment(null);
      setOutcomeData({
        conversion_status: "",
        outcome_notes: "",
        will_register_as_client: false,
        follow_up_required: false,
        follow_up_date: ""
      });
      fetchAppointments();
    } catch (error) {
      console.error('Erro ao salvar resultado:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o resultado",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      prospect_name: "",
      prospect_phone: "",
      prospect_email: "",
      meeting_subject: "",
      appointment_date: "",
      appointment_time: "",
      duration: 60,
      location: "",
      priority: "normal",
      description: "",
      lead_source: "",
      urgency_level: "normal",
      is_billable: false,
      outcome_status: "pending",
      rescheduled_date: "",
      rescheduled_time: "",
      notes: ""
    });
    setSelectedClient("walk_in");
  };

  const handleEdit = (appointment: Appointment) => {
    // Extrair dados da descrição para popular o formulário
    const description = appointment.description || "";
    const phoneMatch = description.match(/Contato: ([^\n]+)/);
    const emailMatch = description.match(/Email: ([^\n]+)/);
    const sourceMatch = description.match(/Fonte: ([^\n]+)/);
    const nameMatch = appointment.title.match(/Atendimento com (.+)/);
    const subjectMatch = description.split('\n')[0];

    // Extrair informações adicionais
    const isBillable = appointment.is_billable || false;
    const outcomeStatus = appointment.outcome_status || "pending";
    const notes = appointment.notes || "";
    
    // Definir o cliente selecionado se existir
    setSelectedClient(appointment.client_id || "walk_in");

    setFormData({
      prospect_name: nameMatch ? nameMatch[1] : appointment.prospect_name || "",
      prospect_phone: phoneMatch ? phoneMatch[1] : appointment.prospect_phone || "",
      prospect_email: emailMatch ? emailMatch[1] : appointment.prospect_email || "",
      meeting_subject: subjectMatch || appointment.meeting_subject || "",
      appointment_date: format(new Date(appointment.appointment_date), 'yyyy-MM-dd'),
      appointment_time: format(new Date(appointment.appointment_date), 'HH:mm'),
      duration: appointment.duration || 60,
      location: appointment.location || "",
      priority: appointment.priority || "normal",
      description: appointment.description?.replace(/Descrição do Atendimento: [^\n]*\n?\n?/, '') || "",
      lead_source: appointment.lead_source || "",
      urgency_level: appointment.urgency_level || "normal",
      is_billable: isBillable,
      outcome_status: outcomeStatus,
      rescheduled_date: "",
      rescheduled_time: "",
      notes: notes
    });

    setEditingAppointment(appointment);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este atendimento?')) return;

    try {
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', id);

      if (error) throw error;
      requestGoogleCalendarSync(); toast({ title: "Sucesso", description: "Atendimento excluído com sucesso" });
      fetchAppointments();
    } catch (error) {
      console.error('Erro ao excluir atendimento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o atendimento",
        variant: "destructive"
      });
    }
  };

  const openOutcomeDialog = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsOutcomeDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      agendado: { color: "bg-blue-500", text: "Agendado" },
      confirmado: { color: "bg-green-500", text: "Confirmado" },
      concluído: { color: "bg-purple-500", text: "Concluído" },
      cancelado: { color: "bg-red-500", text: "Cancelado" },
      reagendado: { color: "bg-yellow-500", text: "Reagendado" },
      remarcado: { color: "bg-orange-500", text: "Remarcado" }
    };

    const config = statusMap[status as keyof typeof statusMap] || { color: "bg-gray-500", text: status };
    
    return (
      <Badge className={`${config.color} text-white`}>
        {config.text}
      </Badge>
    );
  };

  const getConversionStatus = (appointment: Appointment) => {
    if (appointment.conversion_status === "converted") return "Convertido";
    if (appointment.conversion_status === "not_converted") return "Não Convertido";
    if (appointment.conversion_status === "em_analise") return "Em Análise";
    return "Pendente";
  };

  // Estatísticas baseadas nos novos campos
  const totalAppointments = appointments.length;
  const convertedAppointments = appointments.filter(apt => apt.conversion_status === "converted").length;
  const pendingAppointments = appointments.filter(apt => apt.conversion_status === "pending" || !apt.conversion_status).length;
  const conversionRate = totalAppointments > 0 ? ((convertedAppointments / totalAppointments) * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 p-4 md:p-6">
      {/* Cabeçalho */}
      <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-1 duration-300 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7f52]">Relacionamento</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Atendimentos</h1>
          <p className="mt-1 text-sm text-slate-500">Consultas, retornos e novos contatos do escritório.</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingAppointment(null); resetForm(); }} className="h-10 rounded-xl bg-slate-900 px-4 text-[13px] text-white hover:bg-slate-800">
              <Plus className="mr-1.5 h-4 w-4 text-[#e8d5a8]" />
              Agendar atendimento
            </Button>
          </DialogTrigger>
                     <DialogContent
            className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white shadow-2xl border border-[#c4ab83]/30"
            aria-describedby="appointment-form-description"
          >
          <DialogHeader className="relative -m-6 mb-6 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
            {/* Monograma AS como marca d'água */}
            <img
              src="/imgs/logos allana/letra_A_logo.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
            />
            <div className="relative flex items-center gap-4 text-left">
              <img
                src="/imgs/logos allana/letra_A_logo.png"
                alt="Allana Sthéfany"
                className="h-12 w-12 shrink-0 select-none object-contain opacity-80"
              />
              <div className="min-w-0">
                <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">
                  {editingAppointment ? "Editar Atendimento" : "Novo Atendimento"}
                </DialogTitle>
                <p className="mt-1 text-sm text-slate-500">Registre o atendimento com o cliente ou com uma nova pessoa.</p>
              </div>
            </div>
          </DialogHeader>
             <div id="appointment-form-description" className="sr-only">
               {selectedClient === "walk_in" ? "👤 Atendimento Individual" : 
                "👥 Cliente Existente"} - {editingAppointment ? "Editar" : "Agendar"} Atendimento
             </div>
            <form onSubmit={handleFormSubmit} className="space-y-6 relative z-10">
              {/* Seleção de cliente existente ou novo cadastro */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client_select" className="mb-1.5 block text-sm font-medium text-slate-700">Tipo de Atendimento <span className="text-red-500 text-xs">*</span></Label>
                  <Select value={selectedClient} onValueChange={(value) => {
                    setSelectedClient(value);
                    if (value && value !== "walk_in") {
                      const client = clients.find(c => c.id === value);
                      if (client) {
                        setFormData(prev => ({
                          ...prev,
                          prospect_name: client.name,
                          prospect_email: client.email || "",
                          prospect_phone: client.phone || ""
                        }));
                      }
                    } else if (value === "walk_in") {
                      // Limpar dados para atendimento individual
                      setFormData(prev => ({
                        ...prev,
                        prospect_name: "",
                        prospect_email: "",
                        prospect_phone: ""
                      }));
                    }
                  }}>
                    <SelectTrigger className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30">
                      <SelectValue placeholder="Selecione o tipo de atendimento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="walk_in">👤 Atendimento Individual</SelectItem>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          👥 {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="meeting_subject" className="mb-1.5 block text-sm font-medium text-slate-700">Assunto do Atendimento <span className="text-red-500 text-xs">*</span></Label>
                  <Input
                    id="meeting_subject"
                    value={formData.meeting_subject}
                    onChange={(e) => setFormData({...formData, meeting_subject: e.target.value})}
                    required
                    placeholder="Ex: Consulta jurídica, atualização de processo..."
                    className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                  />
                </div>
              </div>

              {/* Campos para atendimento individual */}
              {selectedClient === "walk_in" && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="prospect_name" className="mb-1.5 block text-sm font-medium text-slate-700">Nome <span className="text-red-500 text-xs">*</span></Label>
                      <Input
                        id="prospect_name"
                        value={formData.prospect_name}
                        onChange={(e) => setFormData({...formData, prospect_name: e.target.value})}
                        placeholder="Nome da pessoa"
                        required
                        className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prospect_phone" className="mb-1.5 block text-sm font-medium text-slate-700">Telefone</Label>
                      <Input
                        id="prospect_phone"
                        value={formData.prospect_phone}
                        onChange={(e) => setFormData({...formData, prospect_phone: e.target.value})}
                        placeholder="(81) 99999-9999"
                        className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="appointment_date" className="mb-1.5 block text-sm font-medium text-slate-700">Data <span className="text-red-500 text-xs">*</span></Label>
                  <Input
                    id="appointment_date"
                    type="date"
                    value={formData.appointment_date}
                    onChange={(e) => setFormData({...formData, appointment_date: e.target.value})}
                    required
                    className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                  />
                </div>
                <div>
                  <Label htmlFor="appointment_time" className="mb-1.5 block text-sm font-medium text-slate-700">Horário <span className="text-red-500 text-xs">*</span></Label>
                  <Input
                    id="appointment_time"
                    type="time"
                    value={formData.appointment_time}
                    onChange={(e) => setFormData({...formData, appointment_time: e.target.value})}
                    required
                    className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                  />
                </div>
                <div>
                  <Label htmlFor="duration" className="mb-1.5 block text-sm font-medium text-slate-700">Duração (min)</Label>
                  <Select value={formData.duration.toString()} onValueChange={(value) => setFormData({...formData, duration: parseInt(value)})}>
                    <SelectTrigger className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="60">1 hora</SelectItem>
                      <SelectItem value="90">1h 30min</SelectItem>
                      <SelectItem value="120">2 horas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location" className="mb-1.5 block text-sm font-medium text-slate-700">Local/Formato</Label>
                  <Select value={formData.location} onValueChange={(value) => setFormData({...formData, location: value})}>
                    <SelectTrigger className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30">
                      <SelectValue placeholder="Onde será?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="escritorio">No escritório</SelectItem>
                      <SelectItem value="videochamada">Videochamada</SelectItem>
                      <SelectItem value="cliente">Local do cliente</SelectItem>
                      <SelectItem value="telefone">Por telefone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="priority" className="mb-1.5 block text-sm font-medium text-slate-700">Prioridade</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                    <SelectTrigger className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baixa">Baixa</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                      <SelectItem value="urgente">Urgente</SelectItem>
                      <SelectItem value="critica">Crítica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5">
                  <input
                    type="checkbox"
                    id="is_billable_simple"
                    checked={formData.is_billable}
                    onChange={(e) => setFormData({...formData, is_billable: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="is_billable_simple" className="cursor-pointer text-sm text-slate-700">Atendimento faturável (cobrado do cliente)</Label>
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="mb-1.5 block text-sm font-medium text-slate-700">Observações Adicionais</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Detalhes importantes sobre o caso, expectativas do cliente..."
                  rows={3}
                  className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                />
              </div>


              {/* Campos para resultado e remarcação */}
              <div className="border-t pt-4">
                <h3 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9a7f52]">Resultado e próximos passos</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="outcome_status" className="mb-1.5 block text-sm font-medium text-slate-700">Status Final</Label>
                    <Select value={formData.outcome_status} onValueChange={(value) => setFormData({...formData, outcome_status: value})}>
                      <SelectTrigger className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30">
                        <SelectValue placeholder="Selecione o resultado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Aguardando resultado</SelectItem>
                        <SelectItem value="resolved">Resolvido</SelectItem>
                        <SelectItem value="rescheduled">Remarcado</SelectItem>
                        <SelectItem value="follow_up">Requer acompanhamento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.outcome_status === "rescheduled" && (
                    <>
                      <div>
                        <Label htmlFor="rescheduled_date" className="mb-1.5 block text-sm font-medium text-slate-700">Nova Data</Label>
                        <Input
                          id="rescheduled_date"
                          type="date"
                          value={formData.rescheduled_date}
                          onChange={(e) => setFormData({...formData, rescheduled_date: e.target.value})}
                          className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                        />
                      </div>
                      <div>
                        <Label htmlFor="rescheduled_time" className="mb-1.5 block text-sm font-medium text-slate-700">Novo Horário</Label>
                        <Input
                          id="rescheduled_time"
                          type="time"
                          value={formData.rescheduled_time}
                          onChange={(e) => setFormData({...formData, rescheduled_time: e.target.value})}
                          className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                        />
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <Label htmlFor="notes" className="mb-1.5 block text-sm font-medium text-slate-700">Descrição do Atendimento</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Descreva o que foi discutido, decisões tomadas, próximos passos..."
                    rows={3}
                    className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end border-t border-slate-100 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#c4ab83] font-semibold text-slate-900 hover:bg-[#b39a72]">
                  {editingAppointment ? "Salvar alterações" : "Agendar atendimento"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dashboard e Notificações */}
      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="mb-5 inline-flex h-auto gap-1 rounded-xl bg-slate-100 p-1">
          {[
            { value: 'dashboard', label: 'Visão geral' },
            { value: 'list', label: 'Todos os atendimentos' },
            { value: 'notifications', label: 'Lembretes' },
          ].map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="rounded-lg px-4 py-1.5 text-[13px] text-slate-500 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        
        <TabsContent value="dashboard">
          <AppointmentDashboard />
        </TabsContent>
        
        <TabsContent value="notifications">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <NotificationCenter />
            <CalendarNotificationDemo />
          </div>
        </TabsContent>
        
        <TabsContent value="list">
          {/* Busca e filtros */}
          <div className="mb-4 flex flex-col gap-2 rounded-2xl border border-slate-200/80 bg-white p-2 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Input
                placeholder="Buscar por nome, e-mail ou assunto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-10 rounded-xl border-0 bg-slate-50 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10 w-full rounded-xl border-slate-200 text-sm shadow-none md:w-44">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="agendado">Agendado</SelectItem>
                <SelectItem value="confirmado">Confirmado</SelectItem>
                <SelectItem value="concluído">Concluído</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={conversionFilter} onValueChange={setConversionFilter}>
              <SelectTrigger className="h-10 w-full rounded-xl border-slate-200 text-sm shadow-none md:w-48">
                <SelectValue placeholder="Conversão" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as conversões</SelectItem>
                <SelectItem value="converted">Viraram clientes</SelectItem>
                <SelectItem value="not_converted">Não viraram</SelectItem>
                <SelectItem value="pending">Em aberto</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Lista */}
          {filteredAppointments.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#faf7f2]">
                <Users className="h-5 w-5 text-[#c4ab83]" />
              </div>
              <p className="mt-3 font-medium text-slate-800">Nenhum atendimento encontrado</p>
              <p className="mt-1 text-sm text-slate-500">Agende o primeiro no botão acima.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
              <ul className="divide-y divide-slate-100">
                {filteredAppointments.map((appointment) => {
                  const d = new Date(appointment.appointment_date);
                  const conversion = getConversionStatus(appointment);
                  const statusKey = String(appointment.status || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                  const statusTone: Record<string, string> = {
                    agendado: 'bg-blue-50 text-blue-700',
                    confirmado: 'bg-emerald-50 text-emerald-700',
                    concluido: 'bg-slate-100 text-slate-600',
                    cancelado: 'bg-red-50 text-red-600',
                    reagendado: 'bg-amber-50 text-amber-700',
                    remarcado: 'bg-amber-50 text-amber-700',
                  };
                  const running = timers[appointment.id]?.isRunning;
                  return (
                    <li key={appointment.id} className="group px-5 py-4 transition-colors hover:bg-[#faf7f2]/50">
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl border border-[#c4ab83]/30 bg-[#faf7f2]">
                          <span className="text-[10px] font-semibold uppercase leading-none text-[#9a7f52]">{format(d, 'MMM', { locale: ptBR }).replace('.', '')}</span>
                          <span className="text-lg font-semibold leading-tight tabular-nums text-slate-900">{format(d, 'dd')}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate font-semibold text-slate-900">{appointment.prospect_name || appointment.title}</p>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${statusTone[statusKey] || 'bg-slate-100 text-slate-600'}`}>{appointment.status}</span>
                            {conversion !== 'Pendente' && (
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${conversion === 'Convertido' ? 'bg-[#c4ab83]/20 text-[#7a6440]' : 'bg-slate-100 text-slate-500'}`}>
                                {conversion === 'Convertido' ? 'Virou cliente' : conversion}
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 text-sm text-slate-500">
                            {format(d, "EEEE, dd/MM 'às' HH:mm", { locale: ptBR })}
                            {appointment.duration ? ` · ${appointment.duration} min` : ''}
                            {appointment.meeting_subject ? ` · ${appointment.meeting_subject}` : ''}
                          </p>
                          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                            {appointment.prospect_phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {appointment.prospect_phone}</span>}
                            {appointment.location && <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {appointment.location}</span>}
                          </div>
                          {appointment.description && (
                            <p className="mt-2 line-clamp-2 text-sm text-slate-600">{appointment.description}</p>
                          )}
                          {appointment.outcome_notes && (
                            <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                              <strong>Resultado:</strong> {appointment.outcome_notes}
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2">
                          <div className="flex gap-0.5 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                            {!String(appointment.status || '').toLowerCase().startsWith('conclu') && (
                              <button type="button" onClick={() => openOutcomeDialog(appointment)} className="rounded-lg p-2 text-slate-400 hover:bg-emerald-50 hover:text-emerald-700" title="Registrar resultado">
                                <UserCheck className="h-4 w-4" />
                              </button>
                            )}
                            <button type="button" onClick={() => handleEdit(appointment)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Editar">
                              <Edit className="h-4 w-4" />
                            </button>
                            <button type="button" onClick={() => handleDelete(appointment.id)} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Excluir">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className={`rounded-md px-2 py-0.5 font-mono text-xs tabular-nums ${running ? 'bg-red-50 text-red-700' : 'text-slate-400'}`}>
                              {formatTime(timers[appointment.id]?.elapsed || 0)}
                            </span>
                            <button
                              type="button"
                              onClick={() => running ? stopTimer(appointment.id) : startTimer(appointment.id)}
                              className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${running ? 'text-red-600 hover:bg-red-50' : 'text-[#7a6440] hover:bg-[#c4ab83]/15'}`}
                              title="Cronômetro do atendimento"
                            >
                              {running ? 'Pausar' : 'Iniciar'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </TabsContent>
      </Tabs>

             {/* Dialog de Resultado do Atendimento */}
       <Dialog open={isOutcomeDialogOpen} onOpenChange={setIsOutcomeDialogOpen}>
         <DialogContent
           className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white shadow-2xl border border-[#c4ab83]/30"
           aria-describedby="outcome-form-description"
         >
          <DialogHeader className="relative -m-6 mb-6 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
            {/* Monograma AS como marca d'água */}
            <img
              src="/imgs/logos allana/letra_A_logo.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
            />
            <div className="relative flex items-center gap-4 text-left">
              <img
                src="/imgs/logos allana/letra_A_logo.png"
                alt="Allana Sthéfany"
                className="h-12 w-12 shrink-0 select-none object-contain opacity-80"
              />
              <div className="min-w-0">
                <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">
                  Resultado do Atendimento
                </DialogTitle>
                <p className="mt-1 text-sm text-slate-500">Registre como foi o atendimento e se houve conversão.</p>
              </div>
            </div>
          </DialogHeader>
           <div id="outcome-form-description" className="sr-only">
             Formulário para registrar o resultado do atendimento e status de conversão
           </div>
          <form onSubmit={handleOutcomeSubmit} className="space-y-6 relative z-10">
            <div>
              <Label htmlFor="conversion_status" className="mb-1.5 block text-sm font-medium text-slate-700">Status da Conversão <span className="text-red-500 text-xs">*</span></Label>
              <Select value={outcomeData.conversion_status} onValueChange={(value) => setOutcomeData({...outcomeData, conversion_status: value})} required>
                <SelectTrigger className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30">
                  <SelectValue placeholder="O prospect converteu?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="convertido">Convertido - Virou cliente</SelectItem>
                  <SelectItem value="não convertido">Não convertido</SelectItem>
                  <SelectItem value="em análise">Em análise</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="outcome_notes" className="mb-1.5 block text-sm font-medium text-slate-700">Observações sobre o Resultado <span className="text-red-500 text-xs">*</span></Label>
              <Textarea
                id="outcome_notes"
                value={outcomeData.outcome_notes}
                onChange={(e) => setOutcomeData({...outcomeData, outcome_notes: e.target.value})}
                placeholder="Detalhe como foi o atendimento, o que foi discutido, decisão do cliente..."
                required
                rows={4}
                className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
              />
            </div>

            {outcomeData.conversion_status === "convertido" && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="will_register_as_client"
                  checked={outcomeData.will_register_as_client}
                  onChange={(e) => setOutcomeData({...outcomeData, will_register_as_client: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="will_register_as_client">Cliente convertido - perguntar sobre cadastro completo</Label>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="follow_up_required"
                checked={outcomeData.follow_up_required}
                onChange={(e) => setOutcomeData({...outcomeData, follow_up_required: e.target.checked})}
                className="rounded border-gray-300"
              />
              <Label htmlFor="follow_up_required">Follow-up necessário</Label>
            </div>

            {outcomeData.follow_up_required && (
              <div>
                <Label htmlFor="follow_up_date" className="mb-1.5 block text-sm font-medium text-slate-700">Data para Follow-up</Label>
                <Input
                  id="follow_up_date"
                  type="date"
                  value={outcomeData.follow_up_date}
                  onChange={(e) => setOutcomeData({...outcomeData, follow_up_date: e.target.value})}
                  className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                />
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setIsOutcomeDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#c4ab83] font-semibold text-slate-900 hover:bg-[#b39a72]">
                Salvar resultado
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Appointments;